import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../components/AppIcon';
import { submitFranchiseApplication } from '../services/api';

const FranchisePage = () => {
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    city: '',
    district: '',
    experience: '',
    message: '',
    accept: false,
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    document.title = 'CafeOtomasyonu Bayilik – Bilgilendirme ve Başvuru';
    const meta = document.querySelector('meta[name="description"]') || (() => {
      const m = document.createElement('meta');
      m.setAttribute('name', 'description');
      document.head.appendChild(m);
      return m;
    })();
    meta.setAttribute('content', 'CafeOtomasyonu bayilik başvurusu: komisyon oranları, fiyatlar, giriş ücreti, hedefler ve sağlayacağımız destekler. Formu doldurarak başvurunuzu iletin.');

    const structured = {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'CafeOtomasyonu Bayilik',
      serviceType: 'Franchise / Bayilik Programı',
      areaServed: 'TR',
      provider: { '@type': 'Organization', name: 'Cafe Otomasyonu' },
      offers: {
        '@type': 'Offer',
        price: '7500',
        priceCurrency: 'TRY',
        description: 'Tek seferlik bayilik giriş ücreti. Eğitim, demo erişimi ve bölge temsilciliği dahildir.'
      }
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(structured);
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccess(null);
    setError(null);
    try {
      if (!form.name || !form.phone || !form.email || !form.city || !form.accept) {
        throw new Error('Lütfen zorunlu alanları doldurun ve aydınlatma onayını verin.');
      }
      await submitFranchiseApplication({
        name: form.name,
        phone: form.phone,
        email: form.email,
        city: form.city,
        district: form.district,
        experience: form.experience,
        message: form.message,
        source: 'landing_franchise',
      });
      setSuccess('Başvurunuz alındı. En kısa sürede sizinle iletişime geçeceğiz.');
      setForm({ name: '', phone: '', email: '', city: '', district: '', experience: '', message: '', accept: false });
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Başvuru gönderilemedi. Lütfen daha sonra tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3">
              <Icon name="ArrowLeft" size={24} className="text-gray-600" />
              <span className="font-poppins font-heading-semibold text-lg text-gray-900">Geri Dön</span>
            </Link>
            <h1 className="font-poppins font-heading-bold text-2xl text-gray-900">Bayilik Başvurusu</h1>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-2 gap-10">
          {/* Left: Info */}
          <section>
            <div className="prose max-w-none">
              <h2 className="font-poppins font-heading-bold text-2xl text-foreground mb-4">CafeOtomasyonu Bayilik Bilgilendirme</h2>
              <p className="text-gray-700">CafeOtomasyonu; kafe, restoran, pastane ve benzeri işletmelere yapay zeka destekli dijital çözümler sunar. Bayilik sistemi ile yerel girişimcilere kazanç fırsatı ve hızlı büyüme hedeflenir.</p>

              <h3 className="font-poppins font-heading-semibold text-xl mt-6">Komisyon Oranları</h3>
              <ul className="list-disc pl-6 text-gray-700">
                <li>İlk satışlardan: <strong>%40</strong></li>
                <li>Yenileme satışlarından: <strong>%25</strong></li>
              </ul>
              <p className="text-gray-700">İlk satışla yatırım geri dönüşü; yenilemelerle pasif gelir oluşturulur.</p>

              <h3 className="font-poppins font-heading-semibold text-xl mt-6">Tavsiye Edilen Yazılım Fiyatları</h3>
              <p className="text-gray-700 font-medium">Aylık Paketler</p>
              <ul className="list-disc pl-6 text-gray-700">
                <li>Mini Paket (Sadece QR Menü): <strong>0 TL</strong></li>
                <li>Temel Paket (POS + Online Sipariş): <strong>1.250 TL / ay</strong></li>
                <li>Tüm Paketler (AI + Kurye + Mobil Uygulama): <strong>2.500 TL / ay</strong></li>
              </ul>
              <p className="text-gray-700 font-medium mt-2">Yıllık Paketler (İndirimli)</p>
              <ul className="list-disc pl-6 text-gray-700">
                <li>Temel Paket: <strong>12.000 TL</strong></li>
                <li>Full Paket: <strong>24.000 TL</strong></li>
              </ul>
              <p className="text-gray-700">Bayiler bu fiyatlar üzerinden komisyon kazanır.</p>

              <h3 className="font-poppins font-heading-semibold text-xl mt-6">Bayilik Giriş Ücreti</h3>
              <p className="text-gray-700">Tek seferlik: <strong>7.500 TL</strong></p>
              <p className="text-gray-700 font-medium mt-2">Geri dönüşler:</p>
              <ul className="list-disc pl-6 text-gray-700">
                <li>Eğitim ve tanıtım dökümanları</li>
                <li>Demo panel ve satış destek seti</li>
                <li>Resmî bölge temsilciliği</li>
                <li>Web sitesinde bayi olarak görünürlük</li>
              </ul>

              <h3 className="font-poppins font-heading-semibold text-xl mt-6">Hedef ve Performans Desteği</h3>
              <ul className="list-disc pl-6 text-gray-700">
                <li>İlk yıl zorunlu satış hedefi yok</li>
                <li>İlk 3 ayda en az 2 işletme kazandıranlara ek <strong>%10</strong> satış bonusu</li>
                <li>Dönemsel satış birincilerine ödüller (ör: <strong>10.000 TL</strong> prim)</li>
              </ul>

              <h3 className="font-poppins font-heading-semibold text-xl mt-6">CafeOtomasyonu’nun Sağladıkları</h3>
              <ul className="list-disc pl-6 text-gray-700">
                <li>Teknik ve satış eğitimi</li>
                <li>Hazır tanıtım materyalleri (sunum, broşür, video)</li>
                <li>Demo erişimi</li>
                <li>Sürekli teknik ve satış desteği</li>
                <li>Bayi paneli ile satış ve performans takibi</li>
              </ul>

              <h3 className="font-poppins font-heading-semibold text-xl mt-6">Başvuru Koşulları</h3>
              <ul className="list-disc pl-6 text-gray-700">
                <li>Satış/teknoloji sektöründe deneyim tercih sebebi</li>
                <li>Temsil edilecek şehir/ilçe belirtilmeli</li>
                <li>Bayilik giriş ücreti ödemesi tamamlanmalı</li>
                <li>Değerlerimizi temsil etmeye uygunluk</li>
              </ul>

              <h3 className="font-poppins font-heading-semibold text-xl mt-6">İletişim</h3>
              <p className="text-gray-700">Telefon: +90 507 588 04 81<br />E‑posta: info@cafeotomasyonu.com<br />Web: www.cafeotomasyonu.com<br />WhatsApp: +90 507 588 04 81</p>
            </div>
          </section>

          {/* Right: Form */}
          <section>
            <div className="p-6 rounded-2xl border border-gray-200 bg-white shadow-sm">
              <h2 className="font-poppins font-heading-bold text-xl text-foreground mb-4">Bayilik Başvuru Formu</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ad Soyad *</label>
                  <input name="name" value={form.name} onChange={handleChange} className="w-full border rounded-lg px-3 py-2" placeholder="Ad Soyad" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Telefon *</label>
                    <input name="phone" value={form.phone} onChange={handleChange} className="w-full border rounded-lg px-3 py-2" placeholder="05xx xxx xx xx" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">E‑posta *</label>
                    <input name="email" type="email" value={form.email} onChange={handleChange} className="w-full border rounded-lg px-3 py-2" placeholder="ornek@domain.com" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Şehir *</label>
                    <input name="city" value={form.city} onChange={handleChange} className="w-full border rounded-lg px-3 py-2" placeholder="İl" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">İlçe</label>
                    <input name="district" value={form.district} onChange={handleChange} className="w-full border rounded-lg px-3 py-2" placeholder="İlçe" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Deneyim / Referans</label>
                  <input name="experience" value={form.experience} onChange={handleChange} className="w-full border rounded-lg px-3 py-2" placeholder="Satış/teknoloji deneyimi" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mesaj</label>
                  <textarea name="message" value={form.message} onChange={handleChange} rows="4" className="w-full border rounded-lg px-3 py-2" placeholder="Kısa bir ön yazı"></textarea>
                </div>
                <label className="flex items-center space-x-2 text-sm text-gray-700">
                  <input type="checkbox" name="accept" checked={form.accept} onChange={handleChange} className="rounded" />
                  <span>
                    <Link target='_blank' to="/kvkk" className="text-primary underline">KVKK aydınlatma metnini</Link> okudum ve verilerimin başvuru için işlenmesini onaylıyorum.
                  </span>
                </label>

                {success && <div className="p-3 rounded bg-green-50 text-green-700 text-sm">{success}</div>}
                {error && <div className="p-3 rounded bg-red-50 text-red-700 text-sm">{error}</div>}

                <button type="submit" disabled={loading} className="w-full py-3 rounded-lg bg-primary text-white font-poppins font-heading-semibold hover:opacity-90 transition">
                  {loading ? 'Gönderiliyor...' : 'Başvuruyu Gönder'}
                </button>
              </form>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default FranchisePage;


